import { useState } from "react";
import { Plus, TriangleAlert, X } from "lucide-react";

import type { Step } from "@/lib/integrations/settings";

import { Button, Field, FieldRow, Input, Select } from "./controls";
import styles from "./integrations.module.css";

/**
 * Their value · rows seen · our value. Values the map does not know yet come
 * first, so nothing is missed.
 */
export function ValueMap({
  step,
  seen,
  onChange,
  disabled = false,
  toValue = (text) => text,
}: {
  step: Step;
  /** Every value seen before this step, with how many rows had it. Null while loading. */
  seen: { value: string; rows: number }[] | null;
  onChange: (step: Step) => void;
  disabled?: boolean;
  /** Typed text into the field's type. */
  toValue?: (text: string) => unknown;
}) {
  const [adding, setAdding] = useState("");
  const values = (step.values ?? {}) as Record<string, unknown>;
  const counts = new Map((seen ?? []).map((one) => [one.value, one.rows]));
  const unknown = (seen ?? []).filter((one) => !(one.value in values));
  const known = Object.keys(values);
  const set = (next: Record<string, unknown>) => onChange({ ...step, values: next });
  const text = (value: unknown) => (value === null || value === undefined ? "" : String(value));

  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      {unknown.length > 0 && (
        <p className={styles.warnText} style={{ margin: 0 }}>
          <TriangleAlert size={14} aria-hidden /> {unknown.length} {unknown.length === 1 ? "value is" : "values are"} not in
          the map yet.
        </p>
      )}
      <div className={styles.scrollBox}>
        <table className={styles.miniTable}>
          <thead>
            <tr>
              <th>Their value</th>
              <th>Rows seen</th>
              <th>Our value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {unknown.map((one) => (
              <tr key={`new-${one.value}`}>
                <td className={styles.mono}>
                  <span className={styles.warnText}>
                    <TriangleAlert size={12} aria-label="not in the map" />
                  </span>{" "}
                  {one.value === "" ? "(empty)" : one.value}
                </td>
                <td>{one.rows.toLocaleString()}</td>
                <td colSpan={2}>
                  <Button
                    disabled={disabled}
                    onClick={() => set({ ...values, [one.value]: one.value })}
                    title="Add it with the same value; change it after"
                  >
                    <Plus size={14} aria-hidden />
                    Add to the map
                  </Button>
                </td>
              </tr>
            ))}
            {known.map((their) => (
              <tr key={`known-${their}`}>
                <td className={styles.mono}>{their === "" ? "(empty)" : their}</td>
                <td>{counts.has(their) ? (counts.get(their) ?? 0).toLocaleString() : seen ? "0" : "…"}</td>
                <td>
                  <Input
                    aria-label={`Our value for ${their}`}
                    value={text(values[their])}
                    disabled={disabled}
                    onChange={(event) => set({ ...values, [their]: toValue(event.target.value) })}
                  />
                </td>
                <td>
                  <Button
                    tone="subtle"
                    icon
                    aria-label={`Remove ${their} from the map`}
                    disabled={disabled}
                    onClick={() => {
                      const next = { ...values };
                      delete next[their];
                      set(next);
                    }}
                  >
                    <X size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {known.length === 0 && unknown.length === 0 && (
              <tr>
                <td colSpan={4} className={styles.faint}>
                  {seen === null ? "Reading the values Scout sent…" : "No values yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <FieldRow>
        <Field label="Add a value by hand">
          <Input value={adding} disabled={disabled} placeholder="Their value" onChange={(event) => setAdding(event.target.value)} />
        </Field>
        <Field label=" ">
          <div>
            <Button
              disabled={disabled || adding.trim() === "" || adding in values}
              onClick={() => {
                set({ ...values, [adding]: adding });
                setAdding("");
              }}
            >
              Add
            </Button>
          </div>
        </Field>
      </FieldRow>
      <FieldRow>
        <Field label="Anything else">
          <Select
            value={step.otherwise ?? "keep"}
            disabled={disabled}
            onChange={(event) => onChange({ ...step, otherwise: event.target.value as Step["otherwise"] })}
          >
            <option value="keep">Keep their value as it is</option>
            <option value="default">Use a default</option>
            <option value="empty">Leave the field empty</option>
            <option value="hold">Hold the row and ask a person</option>
          </Select>
        </Field>
        {step.otherwise === "default" && (
          <Field label="Default">
            <Input
              value={text(step.default)}
              disabled={disabled}
              onChange={(event) => onChange({ ...step, default: toValue(event.target.value) })}
            />
          </Field>
        )}
      </FieldRow>
    </div>
  );
}
